import React, { useContext, useEffect, useMemo } from 'react'
import AppPage from '../layouts/AppPage'
import styled from 'styled-components';
import { DataContext, fetchAll } from '../contexts/DataContext';
import { AuthContext } from '../contexts/AuthContext';
import { DEFAULT_TITLE, STATIC_URL } from '../lib/settings';
import { Link } from 'react-router-dom';
import { metaTags } from '../lib/utils/metaTags';

export default function Issues() {
  const { api } = useContext(AuthContext);
  const { issues, setIssues, currentIssueIndex, setCurrentIssueIndex, issueIds, setIssueIds, areIssuesLoaded } = useContext(DataContext);

  useEffect(() => {
    if (areIssuesLoaded.current) return;
    const getIssuesWithPieces = async () => {
      try {
        const data = await fetchAll(api.getIssues, true);
        const issues = Object.fromEntries(data.map(issue => [issue.id, issue]));
        setIssues(issues);
        const issueIds = Object.keys(issues).map(issueId => parseInt(issueId));
        setIssueIds(issueIds);
        setCurrentIssueIndex(issueIds.length - 1);
      } catch (error) {
      } finally {
        areIssuesLoaded.current = true;
      }
    }
    getIssuesWithPieces();
  }, [api, issueIds, setIssues, setIssueIds, currentIssueIndex, setCurrentIssueIndex, areIssuesLoaded]);

  const MetaTags = useMemo(() => {
    const latestIssue = issueIds && issueIds.length > 0 ? issues[issueIds[0]] : null;
    return metaTags({
      title: `${DEFAULT_TITLE} | Все номера`,
      description: latestIssue?.annotation,
      image: latestIssue?.image_path,
      url: 'https://chimeras.world/issues/'
    })
  }, [issueIds, issues]);

  return (
    <AppPage>
      {MetaTags}
      <Wrapper>
        <h2>все номера</h2>
        <ul>
          {issueIds && issueIds.filter((issueId, index) => issueId in issues).map((issueId, index) => issues[issueId]).map((issue, index) => (
            <Issue key={index}>
              <IssueNumber>
                <span>{issue.issue_number}</span>
                <span className='dot' />
                <span>{issue.issue_date}</span>
              </IssueNumber>
              <h1><Link to={`/issues/${issue.id}`}>{issue.title}</Link></h1>
              <IssueMain>
                <Link to={`/issues/${issue.id}`} className="image-container">
                  <img src={`${STATIC_URL}/${issue.image_path}`} alt={issue.title} />
                </Link>
                <section className="annotation">
                  {issue.annotation.split('\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </section>
              </IssueMain>
            </Issue>
          ))}
        </ul>
      </Wrapper>
    </AppPage>
  )
}

const Wrapper = styled.div`
  padding: 50px 20px 0 20px;

  display: flex;
  flex-direction: column;
  gap: 2rem;

  & h2 {
    font-size: 1.125rem;
    font-weight: 700;
    font-family: var(--primary-font);
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 4rem;
    width: 100%;
    padding: 0;
    margin: 0;
    list-style: none;
  }

  @media (min-width: 768px) {
    margin-top: 4rem;
    padding: 0 var(--main-side-padding);
  }
`;

const Issue = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;

  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    font-family: var(--primary-font);
  }

  h1 a {
    color: black;
    text-decoration: none;
  }

  h1 a:hover {
    text-decoration: underline;
    cursor: pointer;
  }

  @media (min-width: 768px) {
    gap: 0;
  }
`;

const IssueNumber = styled.section`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 0.5rem;

  font-size: 1rem;
  font-weight: 700;
  font-family: var(--primary-font);

  & > .dot {
    display: inline-block;
    width: 0.2rem;
    height: 0.2rem;
    border-radius: 50%;
    background-color: black;
  }

  @media (min-width: 768px) {
    margin-bottom: 0.25rem;
    font-size: 1rem;
  }
`;

const IssueMain = styled.section`
  margin-top: 0.75rem;

  display: flex;
  flex-direction: column;
  gap: 1rem;

  .image-container {
    width: 100%;

    img {
      width: 100%;
      height: auto;
    }
  }

  .image-container:hover {
    cursor: pointer;
  }

  & p {
    font: var(--main-text-font);
    min-height: 1rem;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 4rem;

    .image-container {
      flex: 0 0 30%;
    }

    .annotation {
      flex: 0 0 60%;
    }

    & p {
      font-size: 1.25rem;
    }
  }
`;